import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from "@angular/router";
import {
  AccountService,
  AlertService,
  AnalyticsService,
  AppVersionService,
  ApplicationInsightsService,
  AuthService,
  BannerService,
  ConversationsService,
  LinkService,
  SharedService,
  UserAccountService,
  UserService,
  classifyRefreshFailure,
} from "@modules/core";
import { PaymentsService } from "@modules/core/services/payments.service";
import { G2ReviewFlowService } from "@modules/g2-review/g2-review-flow.service";
import { ScribeService } from "@modules/core/services/scribe.service";
import { VoipService } from "@modules/core/services/voip.service";
import {
  BannerActionEvent,
  VisibleBanner,
} from "@modules/shared/banner-carousel/banner-carousel.component";
import { Banner, BannerActionType, FeatureState } from "@types";
import { exponentialRetry, initReconnect } from "@utils";
import Bowser from "bowser";
import { config } from "configurations/config";
import {
  combineLatest,
  filter,
  first,
  firstValueFrom,
  map,
  merge,
  Observable,
  startWith,
  switchMap,
} from "rxjs";
import { generateUpgradeUrl } from "utils/url-utils";
import { PinscreenService } from "./pinscreen/pinscreen.service";

const MIN_SCREEN_WIDTH = 500;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnDestroy, OnInit {
  private _browser = Bowser.getParser(navigator.userAgent);

  isMobile = this._browser.getPlatformType() === "mobile";
  isBrowserSupported = this.getIsBrowserSupported();
  isBigScreen = window.innerWidth > MIN_SCREEN_WIDTH;
  navHidden: boolean = true;
  focused = true;

  public upgradeUrl = generateUpgradeUrl();

  // TODO connect to storage/backend/etc to track if user is free and if the banner should be shown
  public isBannerDismissed$ = this.bannerService.isBannerDismissed$;
  public isAllowedToDisplayUpgrade$ = this.bannerService.isAllowedToDisplay$;

  private hasCheckedTrialCta = false;

  public isProcessing$: Observable<boolean> = this.router.events
    .pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url)
    )
    .pipe(
      startWith(window.location.pathname),
      map((url) => {
        return url.startsWith("processing") || url.startsWith("/processing");
      })
    );

  public banners$ = this.bannerService.banners$;
  public g2ReviewCheck$ = this.g2ReviewFlowService.checkState$;
  public updateAvailable$ = this.appVersionService.updateAvailable$;
  private previousBannerInstanceId: string | null = null;
  private focusAuthRecovery: Promise<void> | null = null;

  constructor(
    private route: ActivatedRoute,
    private pinScreenService: PinscreenService,
    public sharedService: SharedService,
    public linkService: LinkService,
    public alertService: AlertService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private userAccountService: UserAccountService,
    private router: Router,
    private dialog: MatDialog,
    private applicationInsightsService: ApplicationInsightsService,
    private conversationsService: ConversationsService,
    private voipService: VoipService,
    private bannerService: BannerService,
    private paymentsService: PaymentsService,
    private userService: UserService,
    private accountService: AccountService,
    private analyticsService: AnalyticsService,
    private scribeService: ScribeService,
    private appVersionService: AppVersionService,
    private g2ReviewFlowService: G2ReviewFlowService
  ) {}

  ngOnInit() {
    this.appVersionService.startPolling();

    // Preload user limits and scribe interactions for faster guard performance
    this.authService.isAuthenticated$
      .pipe(
        first((isAuthenticated) => isAuthenticated),
        switchMap(() => this.accountService.getUserLimits()),
        switchMap((userLimits) => {
          // Only preload scribe interactions if user has any scribe access
          if (
            userLimits?.scribe?.featureState === FeatureState.Enabled ||
            userLimits?.scribe?.featureState === FeatureState.Upgradeable
          ) {
            return this.scribeService.getInteractions();
          }
          return [];
        })
      )
      .subscribe();

    // Refresh user limits after a trial is started or ends
    merge([
      this.paymentsService.trialEnded$,
      this.paymentsService.trialStarted$,
    ])
      .pipe(
        switchMap(() =>
          this.accountService.getUserLimits().pipe(exponentialRetry())
        )
      )
      .subscribe();

    combineLatest([
      this.authService.isAuthenticated$,
      this.pinScreenService.isLocked$,
      this.authService.hasCompletedOnboarding$,
    ])
      .pipe(
        first(
          ([isAuthenticated, isLocked, hasCompletedOnboarding]) =>
            isAuthenticated && !isLocked && hasCompletedOnboarding
        ),
        switchMap(() => {
          return this.userService.getUserWorkspaces({
            pageSize: 1,
          });
        })
      )
      .subscribe({
        next: (workspaces) => {
          const isOnboarding = this.authService.getIsOnboarding();
          this.authService.removeIsOnboarding();

          // If a user already has any workspaces by the time they get here then we assume they've added some as
          // part of onboarding on the /onboard/workspace-add route
          if (!isOnboarding || workspaces.data?.length) {
            this.bannerService.updateIsBannerDismissed();
            this.bannerService.setIsAllowedToDisplay(true);
            this.hasCheckedTrialCta = true;
            return;
          }

          this.paymentsService
            .openStartTrialCtaDialog()
            .afterClosed()
            .subscribe({
              next: () => {
                this.hasCheckedTrialCta = true;
              },
            });
        },
      });

    // Check for the G2 review prompt once per page load, after the PIN screen.
    // A page load covers both a fresh open and a completed login: logging out
    // always leaves the page, so a login returns through a new page load.
    // Unlocking with a PIN later in the same page load never re-checks.
    // Notifications only focus an already open tab, and refocusing never
    // reloads, so neither reaches this. The call window and processing page
    // are separate page loads, so skip them.
    combineLatest([
      this.authService.isAuthenticated$,
      this.pinScreenService.isLocked$,
      this.authService.hasCompletedOnboarding$,
    ])
      .pipe(
        first(
          ([isAuthenticated, isLocked, hasCompletedOnboarding]) =>
            isAuthenticated && !isLocked && hasCompletedOnboarding
        )
      )
      .subscribe(() => {
        const path = window.location.pathname;
        if (path.startsWith("/call") || path.startsWith("/processing")) return;
        this.g2ReviewFlowService.promptIfEligible();
      });

    this.applicationInsightsService.initialize();
    initReconnect();

    this.logUnsupportedViewIfShown();

    this.registerIcons(this.iconRegistry, this.sanitizer);

    this.pinScreenService.isLocked$.subscribe({
      next: () => {
        this.handleInvitationCode();
      },
    });

    // This is here to support legacy components/services that assume
    // a user's account is initially loaded
    this.authService.userId$.subscribe({
      next: (userId) => {
        if (!userId) return;
        this.userAccountService.getUserAccount(true);
      },
    });

    this.sharedService.onFocusChange.subscribe(async (focused) => {
      this.focused = focused;
      if (focused) {
        await this.onRegainFocus();
      }
    });

    this.router.events.subscribe({
      next: (routerEvent) => {
        if (this.route.children.some((c) => c.routeConfig.data?.isNotFound)) {
          this.navHidden = true;
          return;
        }

        if (routerEvent instanceof NavigationEnd) {
          this.navHidden = false;
          const url = routerEvent.url;
          if (
            url.startsWith("/login") ||
            url.startsWith("/signin-oidc") ||
            url.startsWith("/?") ||
            url.startsWith("/invite?") ||
            url.startsWith("/signout-callback-oidc") ||
            url.startsWith("/pin") ||
            url.startsWith("/set-password") ||
            url.startsWith("/onboard/workspace-add") ||
            url.startsWith("/unauthorized") ||
            url.startsWith("/register-login-oidc") ||
            url == "/" ||
            url == ""
          ) {
            this.navHidden = true;
          } else {
            this.authService.setHasCompletedOnboarding();
            this.bannerService.refreshBanners();
          }
        } else if (routerEvent instanceof NavigationStart) {
          if (this.hasCheckedTrialCta) {
            this.dialog.closeAll();
          }

          if (!routerEvent.url.startsWith("/network"))
            this.sharedService.setTitle(config.title);
        }
      },
    });

    this.conversationsService.unreadConversationIds$.subscribe({
      next: (unreadConversationIds) => {
        this.sharedService.updateRemoteUnreadConversationIds(
          unreadConversationIds
        );
      },
    });
  }

  handleInvitationCode() {
    // Invite code is stored in auth service during auth initialization
    const invitationCode = this.authService.getInvitationCode();
    if (
      !invitationCode ||
      !this.authService.isAuthenticated() ||
      this.pinScreenService.isLocked() ||
      this.router.url.indexOf("/onboard/") != -1
    ) {
      return;
    }
    this.authService.removeInvitationCode();
    this.linkService.handleInviteCode(invitationCode, true);
  }

  ngOnDestroy() {
    this.userAccountService.clearMemory();
  }

  async onRegainFocus() {
    if (this.router.url.indexOf("/onboard/") != -1) return;

    if (!this.sharedService.isOnline()) return;

    await this.handleRegainedFocusState();
  }

  handleRegainedFocusState(): Promise<void> {
    if (!this.focusAuthRecovery) {
      this.focusAuthRecovery = this.recoverAuthenticationOnFocus().finally(
        () => {
          this.focusAuthRecovery = null;
        }
      );
    }

    return this.focusAuthRecovery;
  }

  private async recoverAuthenticationOnFocus(): Promise<void> {
    if (this.authService.isAuthenticated()) return;

    if (this.router.url == "/") return;

    const origin = this.router.url;
    this.applicationInsightsService.trackTrace(
      "Unauthenticated on focus regain",
      { origin }
    );

    if (this.authService.getRefreshToken()) {
      this.applicationInsightsService.trackTrace(
        "Token refresh started on focus regain",
        { origin }
      );

      try {
        await firstValueFrom(this.authService.refreshToken());
      } catch (error: unknown) {
        const classification = classifyRefreshFailure(error);
        this.applicationInsightsService.trackTrace(
          "Token refresh failed on focus regain",
          { origin, classification }
        );

        // Redirecting to the identity provider is a full page navigation that
        // loses whatever the user was doing. Only do that when the auth server
        // has actually rejected the grant. A refresh that could not reach the
        // server leaves a usable refresh token, so stay where we are and let
        // the auth interceptor refresh again — and if the network is still
        // down, a navigation would only land on a browser error page.
        if (classification !== "session-ended") return;
      }

      if (this.authService.isAuthenticated()) {
        this.applicationInsightsService.trackTrace(
          "Token refresh success on focus regain",
          { origin }
        );
        return;
      }
    }

    try {
      await this.authService.loginWithoutPrompt({
        disableClearingLocalStorageBeforeLogin: true,
      });
    } catch {
      this.applicationInsightsService.trackTrace(
        "Login failed on focus regain",
        { origin }
      );
      this.router.navigate(["/"]);
    }
  }

  @HostListener("window:resize")
  onWindowResize() {
    this.isBigScreen = window.innerWidth > MIN_SCREEN_WIDTH;
  }

  @HostListener("document:mouseleave", ["$event"])
  onMouseLeave(e) {}

  login() {
    this.sharedService.navigateTo("/");
  }

  isCall(): boolean {
    const urlArray = this.router.url.split("/");
    return urlArray[urlArray.length - 1].split("?")[0] === "call";
  }

  private getIsBrowserSupported(): boolean {
    if (this.isMobile) {
      return false;
    }

    const satisfies = this._browser.satisfies({
      chrome: ">=60",
      "microsoft edge": ">=16",
    });

    if (satisfies === false) {
      return false;
    }

    return satisfies === true || this._browser.getEngineName() === "Blink";
  }

  registerIcons(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry.addSvgIcon(
      "celo-network",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/overrides-icons-icon-1.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "message",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/glyphs-messages-white.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "message-orange",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/glyphs-create-new.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "remove-red",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/remove-photo.svg")
    );
    iconRegistry.addSvgIcon(
      "photos",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-photosv2.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "photos-white",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-photos-whitev2.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "documents",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-documentsv2.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "consents",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-consentsv2.png"
      )
    );
    iconRegistry.addSvgIcon(
      "home_outline",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-home.svg")
    );

    iconRegistry.addSvgIcon(
      "email_outline",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-email.svg")
    );

    iconRegistry.addSvgIcon(
      "phone_outline",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-phone.svg")
    );

    iconRegistry.addSvgIcon(
      "lookup-button",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/lookup-button.svg"
      )
    );

    iconRegistry.addSvgIcon(
      "ic-emr",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-emr.svg")
    );

    iconRegistry.addSvgIcon(
      "ic-emr-red",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-emr.svg")
    );

    iconRegistry.addSvgIcon(
      "new-case",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-case.svg")
    );
    iconRegistry.addSvgIcon(
      "new-group",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-group.svg")
    );
    iconRegistry.addSvgIcon(
      "new-external",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/new-external.svg")
    );

    iconRegistry.addSvgIcon(
      "icon-dnd",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-dnd.svg")
    );

    iconRegistry.addSvgIcon(
      "icon-info",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-info.svg")
    );

    iconRegistry.addSvgIcon(
      "icon-privacy",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-privacy.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-security",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-security.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-contactus",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-contactus.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-userdocuments",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-userdocuments.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-invitecolleagues",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-invitecolleagues.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-helpcenter",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-helpcenter.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-verified",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-verified.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-unverified",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-unverified.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-verified-workspace",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-verified-workspace.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-message",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/message.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-documents-consents",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-save-send.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-pin",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-pin.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-unpin",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-unpin.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-mute",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-mute.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-unmute",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-unmute.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-clear-badge",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-clear-badge.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-archive",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-archive.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-notifications",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-notifications.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-arrow-down",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-arrow-down.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-close",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-close.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-video",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-video.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-share-link",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-share-link.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "ic-group",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-group.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-export",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-export.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-settings",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-settings.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-case",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-case.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-remove-users",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-remove-users.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-edit",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-edit.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-add-outline",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-add-outline.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-join-workspace",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-join-workspace.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "ic-workspace-member",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-workspace-member.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "ic-add-colleagues",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-add-colleagues.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "admin-settings",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/admin-settings.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "ic-share",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-share.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-reset",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-reset.svg")
    );
    iconRegistry.addSvgIcon(
      "avatar-patient",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/avatar-patient.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "avatar-group",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/avatar-group.svg")
    );
    iconRegistry.addSvgIcon(
      "avatar-workspace",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/avatar-workspace.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "add-filled",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/ic-add-filled.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "ic-roles",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-roles.svg")
    );
    iconRegistry.addSvgIcon(
      "ic-alert",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-alert.svg")
    );
    iconRegistry.addSvgIcon(
      "video-on",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/video-on.svg")
    );
    iconRegistry.addSvgIcon(
      "video-off",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/video-off.svg")
    );

    iconRegistry.addSvgIcon(
      "icon-attach",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/icon-attach.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-secure-library",
      sanitizer.bypassSecurityTrustResourceUrl(
        "/assets/icons/icon-secure-library.svg"
      )
    );
    iconRegistry.addSvgIcon(
      "icon-emoji",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-emoji.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-photo",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-photo.svg")
    );
    iconRegistry.addSvgIcon(
      "icon-scribe",
      sanitizer.bypassSecurityTrustResourceUrl("/assets/icons/ic-scribe.svg")
    );
  }

  public handleDismissBanner(banner: Banner) {
    this.bannerService.dismissBanner(banner);
    this.analyticsService
      .logEvent({
        eventType: "BannerDismiss",
        timestamp: new Date().toISOString(),
        banner,
      })
      .subscribe();
  }

  public handleBannerAction({ banner, action }: BannerActionEvent) {
    this.analyticsService
      .logEvent({
        eventType: "BannerClick",
        timestamp: new Date().toISOString(),
        banner,
        bannerAction: action,
      })
      .subscribe();

    if (action.type === BannerActionType.Reload) {
      window.location.reload();
    }
  }

  public handleVisibleBanner({ banner, instanceId }: VisibleBanner) {
    if (this.previousBannerInstanceId === instanceId) return;
    this.previousBannerInstanceId = instanceId;
    this.analyticsService
      .logEvent({
        eventType: "BannerView",
        timestamp: new Date().toISOString(),
        banner,
      })
      .subscribe();
  }

  copyDebugInfo() {
    const info = [
      `UA: ${navigator.userAgent}`,
      `Browser: ${JSON.stringify(this._browser.getResult(), null, 2)}`,
      `Screen: ${window.innerWidth}x${window.innerHeight} (devicePixelRatio: ${window.devicePixelRatio})`,
    ].join("\n\n");
    navigator.clipboard.writeText(info);
  }

  private logUnsupportedViewIfShown() {
    if (this.isBrowserSupported && this.isBigScreen) {
      return;
    }

    this.analyticsService
      .logEvent({
        eventType: this.isBrowserSupported
          ? "UnsupportedScreenView"
          : "UnsupportedBrowserView",
        timestamp: new Date().toISOString(),
      })
      .subscribe();
  }
}
